/**
 * Enhanced Add-to-Cart Integration - Phase 5
 * Improvements:
 * 1. Fixed step numbering logic
 * 2. File upload state detection and messaging
 * 3. Sticky add-to-cart button with price display
 */
(() => {
  'use strict';

  // System state and configuration
  let initialized = false;
  let addToCartForm = null;
  let addToCartButton = null;
  let mainEnhancedButton = null; // NEW: Main enhanced button reference
  let celebrationHeader = null;
  let stickyContainer = null;
  let currentState = 'initializing';
  let stepNumber = 2;
  let validationCache = new Map();
  let performanceMetrics = {
    stateChanges: 0,
    headerUpdates: 0,
    buttonInteractions: 0
  };

  const stateManager = window.PitchPrintStateManager;
  const log = (...args) => window.PITCHPRINT_DEBUG && console.log('[EnhancedAddToCart]', ...args);

  if (!stateManager) {
    console.error('[EnhancedAddToCart] StateManager not found - enhanced integration disabled');
    return;
  }

  // Enhanced button states with file upload support
  const ENHANCED_STATES = {
    NEED_DESIGN: 'need_design',        // No project - guide to start designing
    NEED_FILE_UPLOAD: 'need_file_upload', // File upload required
    SENDING_LATER: 'sending_later',    // User selected "Send Files Later"
    FILES_UPLOADED: 'files_uploaded',  // Files uploaded - ready for cart (file upload method)
    READY_TO_ADD: 'ready_to_add',      // Project exists - ready for cart (PitchPrint method)
    STANDARD_PRODUCT: 'standard_product', // Non-customizable product - ready to add
    VALIDATING: 'validating',          // Checking project/form
    PROCESSING: 'processing',          // Adding to cart
    SUCCESS: 'success',                // Successfully added
    ERROR: 'error',                    // Error occurred
    DISABLED: 'disabled',              // Temporarily disabled
    // Quick Design flow states (v4.2.0)
    QUICK_CHOOSE_FRONT: 'quick_choose_front',
    QUICK_CHOOSE_BACK: 'quick_choose_back',
    QUICK_PREVIEW: 'quick_preview'
  };

  // State configuration with file upload messaging
  const STATE_CONFIG = {
    [ENHANCED_STATES.NEED_DESIGN]: {
      buttonText: 'Next: Customize Your Design',
      buttonEnabled: true, // Changed to true - button should be clickable
      buttonClass: 'start-designing',
      headerTitle: '🎨 Ready to Design?',
      headerSubtitle: 'Customize your design with the Studio Design tool',
      headerOverline: 'NEXT STEP'
    },
    [ENHANCED_STATES.NEED_FILE_UPLOAD]: {
      buttonText: 'Next: Upload Your Files',
      buttonEnabled: true, // Changed to true - button should be clickable to scroll to upload
      buttonClass: 'need-upload',
      headerTitle: '📁 Files Required',
      headerSubtitle: 'Upload your artwork to continue',
      headerOverline: 'NEXT STEP'
    },
    [ENHANCED_STATES.SENDING_LATER]: {
      buttonText: 'Add to Cart',
      buttonEnabled: true,
      buttonClass: 'sending-later',
      headerTitle: '📧 Files Will Be Sent Later',
      headerSubtitle: 'We\'ll reach out after your order to collect your artwork',
      headerOverline: 'FINAL STEP'
    },
    [ENHANCED_STATES.FILES_UPLOADED]: {
      buttonText: 'Add to Cart',
      buttonEnabled: true,
      buttonClass: 'files-uploaded',
      headerTitle: '🎉 You\'re Almost There!',
      headerSubtitle: 'Your custom uploads are complete • Let\'s make it official! ✨',
      headerOverline: 'FINAL STEP',
      helperText: 'Your custom files are uploaded'
    },
    [ENHANCED_STATES.READY_TO_ADD]: {
      buttonText: 'Add to Cart',
      buttonEnabled: true,
      buttonClass: 'ready-to-add',
      headerTitle: '🎉 You\'re Almost There!',
      headerSubtitle: 'Your custom design is ready • Let\'s make it official! ✨',
      headerOverline: 'FINAL STEP',
      helperText: 'Your custom design is saved'
    },
    [ENHANCED_STATES.STANDARD_PRODUCT]: {
      buttonText: 'Add to Cart',
      buttonEnabled: true,
      buttonClass: 'standard-product',
      headerTitle: '🎉 You\'re Almost There!',
      headerSubtitle: 'Add this item to your cart before someone else snags it!',
      headerOverline: 'FINAL STEP',
      helperText: 'Add this item to your cart'
    },
    [ENHANCED_STATES.VALIDATING]: {
      buttonText: 'Checking Design...',
      buttonEnabled: false,
      buttonClass: 'validating',
      headerTitle: '🔍 Validating Design',
      headerSubtitle: 'Making sure everything looks perfect...',
      headerOverline: 'PROCESSING...'
    },
    [ENHANCED_STATES.PROCESSING]: {
      buttonText: 'Adding to Cart...',
      buttonEnabled: false,
      buttonClass: 'processing',
      headerTitle: '⚡ Almost Done!',
      headerSubtitle: 'Adding your custom design to cart...',
      headerOverline: 'FINAL STEP'
    },
    [ENHANCED_STATES.SUCCESS]: {
      buttonText: 'Added Successfully!',
      buttonEnabled: false,
      buttonClass: 'success',
      headerTitle: '✅ Success!',
      headerSubtitle: 'Your custom design has been added to cart!',
      headerOverline: 'COMPLETE'
    },
    [ENHANCED_STATES.ERROR]: {
      buttonText: 'Try Again',
      buttonEnabled: true,
      buttonClass: 'error',
      headerTitle: '⚠️ Let\'s Try Again',
      headerSubtitle: 'Something went wrong. Please try again or contact support.',
      headerOverline: 'RUH ROH!'
    },
    [ENHANCED_STATES.DISABLED]: {
      buttonText: 'Unavailable',
      buttonEnabled: false,
      buttonClass: 'disabled',
      headerTitle: '🚫 Temporarily Unavailable',
      headerSubtitle: 'Please refresh the page and try again.',
      headerOverline: 'RUH ROH!'
    },
    // Quick Design flow states (v4.2.0)
    [ENHANCED_STATES.QUICK_CHOOSE_FRONT]: {
      buttonText: 'Next: Choose Front Design',
      buttonEnabled: true,
      buttonClass: 'quick-choose-front',
      headerTitle: '🎨 Choose Your Front Design',
      headerSubtitle: 'Select a design to get started',
      headerOverline: 'STEP 1',
      helperText: 'Choose a design for the front of your sign'
    },
    [ENHANCED_STATES.QUICK_CHOOSE_BACK]: {
      buttonText: 'Next: Choose Back Design',
      buttonEnabled: true,
      buttonClass: 'quick-choose-back',
      headerTitle: '🔄 Now Choose Your Back Design',
      headerSubtitle: 'Select the design for the back of your sign',
      headerOverline: 'STEP 2',
      helperText: 'Choose a design for the back of your sign'
    },
    [ENHANCED_STATES.QUICK_PREVIEW]: {
      buttonText: 'Next: Preview and Approve',
      buttonEnabled: true,
      buttonClass: 'quick-preview',
      headerTitle: '👀 Preview Your Design',
      headerSubtitle: 'Click below to preview before adding to cart',
      headerOverline: 'FINAL STEP',
      helperText: 'Review and approve your design'
    }
  };

  // =====================================
  // INITIALIZATION & SETUP
  // =====================================

  const init = () => {
    if (initialized) {
      log('Already initialized, skipping...');
      return;
    }
    initialized = true;
    log('🚀 Initializing Enhanced Add-to-Cart Integration...');

    if (!findFormAndButton()) {
      log('❌ Cannot initialize without form/button - will retry...');
      setTimeout(init, 500);
      return;
    }

    calculateStepNumber();
    injectEnhancedStyles();
    setupStateManagerListeners();
    setupFormHandling();
    setupPitchPrintMonitoring();
    setupFileUploadMonitoring();
    setupQuantityMonitoring(); // NEW: Monitor quantity selector changes
    setupStickyContainer();
    setupMainEnhancedButtonHandlers(); // NEW: Setup main enhanced button
    setupMobileDockObserver();

    // CRITICAL: Ensure structure exists IMMEDIATELY on page load
    ensureEnhancedStructure();

    setupButtonReplacementWatcher(); // NEW: Watch for variant-selects replacing button

    setTimeout(() => {
      // Double-check structure exists before state updates
      ensureEnhancedStructure();

      performInitialStateAnalysis();
      injectCelebrationHeader();
      setupScrollHandler();

      // Debug sticky button visibility
      setTimeout(() => {
        const sticky = document.querySelector('.configurator-sticky-cart-container');
        log('=== STICKY BUTTON DEBUG ===');
        log('Sticky container found:', !!sticky);
        if (sticky) {
          log('Sticky classes:', sticky.className);
          const styles = getComputedStyle(sticky);
          log('Sticky computed styles:', {
            display: styles.display,
            opacity: styles.opacity,
            transform: styles.transform,
            position: styles.position,
            bottom: styles.bottom,
            zIndex: styles.zIndex,
            pointerEvents: styles.pointerEvents
          });
          log('Sticky parent:', sticky.parentElement?.className);
          log('Sticky parent styles:', {
            position: getComputedStyle(sticky.parentElement).position,
            overflow: getComputedStyle(sticky.parentElement).overflow
          });
        }

        // Check original button position
        const origBtn = document.querySelector('.product-form__submit');
        if (origBtn) {
          const rect = origBtn.getBoundingClientRect();
          log('Original button position:', {
            top: rect.top,
            bottom: rect.bottom,
            windowHeight: window.innerHeight,
            isVisible: rect.top >= 0 && rect.bottom <= window.innerHeight
          });
        }

        // Force show for testing
        if (sticky) {
          log('Testing: Force showing sticky button...');
          sticky.classList.remove('configurator-loading');
          sticky.classList.add('visible');
          const newStyles = getComputedStyle(sticky);
          log('After force-show - opacity:', newStyles.opacity, 'pointerEvents:', newStyles.pointerEvents);

          // Check positioning and viewport
          const rect = sticky.getBoundingClientRect();
          log('Sticky position in viewport:', {
            top: rect.top,
            bottom: rect.bottom,
            left: rect.left,
            right: rect.right,
            width: rect.width,
            height: rect.height,
            isInViewport: rect.top >= 0 && rect.bottom <= window.innerHeight && rect.left >= 0 && rect.right <= window.innerWidth
          });

          // Check if anything is covering it
          const elementAtPoint = document.elementFromPoint(rect.left + rect.width/2, rect.top + rect.height/2);
          log('Element at sticky center point:', elementAtPoint?.className, elementAtPoint?.tagName);

          // Check z-index chain
          let parent = sticky.parentElement;
          const zIndexChain = [];
          while (parent && parent !== document.body) {
            const parentStyles = getComputedStyle(parent);
            zIndexChain.push({
              element: parent.className || parent.tagName,
              zIndex: parentStyles.zIndex,
              position: parentStyles.position
            });
            parent = parent.parentElement;
          }
          log('Z-index chain:', zIndexChain);
        }
      }, 1000);
    }, 100);

    log('✅ Enhanced Add-to-Cart Integration ready');
  };

  const findFormAndButton = () => {
    const formSelectors = [
      '.product-form',
      'form[action*="/cart/add"]',
      'form[action="/cart/add"]'
    ];

    const buttonSelectors = [
      '.product-form__submit',
      'button[name="add"]',
      'button[type="submit"]'
    ];

    for (const selector of formSelectors) {
      addToCartForm = document.querySelector(selector);
      if (addToCartForm) {
        log(`✅ Form found: ${selector}`);
        break;
      }
    }

    for (const selector of buttonSelectors) {
      addToCartButton = document.querySelector(selector);
      if (addToCartButton) {
        log(`✅ Button found: ${selector}`);
        break;
      }
    }

    // NEW: Find the main enhanced button (has specific ID)
    mainEnhancedButton = document.getElementById('main-enhanced-cart-button');
    if (mainEnhancedButton) {
      log('✅ Main enhanced button found');
    } else {
      log('⚠️ Main enhanced button not found - will use legacy button only');
    }

    return addToCartForm && addToCartButton;
  };

  const calculateStepNumber = () => {
    // Get all option groups (Sides, Shape, Size)
    const optionGroups = document.querySelectorAll('.option-group, [data-option-index]');
    const optionsCount = optionGroups.length;

    // Count additional steps
    const usesPitchPrint = isPitchPrintProduct();
    const usesFileUpload = isFileUploadProduct();

    // Base step number starts after variant options
    let additionalSteps = 0;
    if (usesPitchPrint) additionalSteps++;
    if (usesFileUpload) additionalSteps++;

    // The add-to-cart button is the final step
    stepNumber = optionsCount + additionalSteps + 1;

    log(`📊 Calculated step number: ${stepNumber} (options: ${optionsCount}, PitchPrint: ${usesPitchPrint}, FileUpload: ${usesFileUpload})`);
  };

  // =====================================
  // PRODUCT ATTRIBUTE GETTER
  // =====================================

  const getCurrentVariantAttributes = () => {
    // Try multiple approaches to get variant options

    // APPROACH 1: Read from the actual select elements (populated by card-variants)
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const selects = productForm.querySelectorAll('select[name^="options["]');
      log('🔍 [Enhanced] Found', selects.length, 'select elements in form');

      if (selects.length > 0) {
        const allOptions = [];

        selects.forEach(select => {
          const nameMatch = select.name.match(/options\[(.*?)\]/);
          if (!nameMatch) return;

          const optionName = nameMatch[1];
          const value = select.value;

          log(`  📝 Option: "${optionName}" = "${value}"`);
          allOptions.push({ name: optionName, value: value });
        });

        if (allOptions.length > 0) {
          // Build display string with all options
          const display = allOptions.map(opt => opt.value).join(' • ');
          log('✅ [Enhanced] Extracted from selects:', { allOptions, display });
          return { allOptions, display };
        }
      }
    }

    // APPROACH 2: Get ALL currently selected cards (active cards)
    log('⚠️ [Enhanced] Selects approach failed, trying active cards...');

    // Find all active variant cards
    const activeCards = document.querySelectorAll('.card-p-style.active');
    log('🔍 [Enhanced] Found', activeCards.length, 'active cards');

    const allOptions = [];

    activeCards.forEach(card => {
      const value = card.dataset.value;
      const optionGroup = card.closest('[data-option-name]');

      if (optionGroup && value) {
        const optionName = optionGroup.dataset.optionName;
        log(`  📝 Active card: "${optionName}" = "${value}"`);
        allOptions.push({ name: optionName, value: value });
      }
    });

    // Build display string with all options
    const display = allOptions.length > 0
      ? allOptions.map(opt => opt.value).join(' • ')
      : 'Standard';

    log('✅ [Enhanced] Extracted from cards:', { allOptions, display });

    return {
      allOptions,
      display
    };
  };

  // =====================================
  // STICKY ADD-TO-CART CONTAINER
  // =====================================

  const setupStickyContainer = () => {
    if (!addToCartButton) return;

    // Find the product info/form container to attach sticky button to
    const productInfoContainer = document.querySelector('.product__info-wrapper') ||
                                  document.querySelector('.product__info') ||
                                  document.querySelector('.product-form') ||
                                  addToCartForm?.parentElement;

    if (!productInfoContainer) {
      log('❌ Cannot find product info container for sticky button');
      return;
    }

    // Create sticky container with correct classes
    stickyContainer = document.createElement('div');
    stickyContainer.className = 'configurator-sticky-cart-container right-positioned configurator-loading';

    // Create sticky button with cart icon, product attributes, and pricing
    const stickyButton = document.createElement('button');
    stickyButton.type = 'button';
    stickyButton.className = 'btn btn--primary btn--large sticky-cart-button';
    stickyButton.innerHTML = `
      <div class="sticky-cart-content">
        <div class="sticky-cart-main">
          <div class="sticky-cart-main-content">
            <span class="btn-icon btn-icon-cart">
              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 19' width='18' height='19'>
                <g focusable='false'>
                  <path fill='#fff' d='M5.2,15.1c-.4,0-.9-.1-1.2-.4-.4-.3-.7-.8-.8-1.4L2,2.9c0,0-.3-.2-1.5-.2h-.1S.4.7.4.7h.1c.8,0,3.2,0,3.5,2l.3,2.4h12.1c.7,0,1.2.5,1.2,1.2v5.4c0,1-.7,1.8-1.7,2-2.2.4-3.8.6-5.4.8-1.5.2-3,.3-5,.7-.1,0-.2,0-.3,0ZM4.5,7l.7,6.1c2.1-.4,3.7-.5,5.2-.7,1.5-.2,3.1-.4,5.3-.7v-4.6H4.5Z'/>
                  <path fill='#fff' d='M13.4,17c0,.7.6,1.3,1.3,1.3s1.3-.6,1.3-1.3-.6-1.3-1.3-1.3-1.3.6-1.3,1.3Z'/>
                  <path fill='#fff' d='M3.8,17c0,.7.6,1.3,1.3,1.3s1.3-.6,1.3-1.3-.6-1.3-1.3-1.3-1.3.6-1.3,1.3Z'/>
                </g>
              </svg>
            </span>
            <img class="btn-icon btn-icon-design" src="https://cdn.shopify.com/s/files/1/0828/3865/1157/files/paintbrush.svg?v=1761076852" alt="Design Studio" loading="lazy" style="display: none;">
            <img class="btn-icon btn-icon-upload" src="https://cdn.shopify.com/s/files/1/0828/3865/1157/files/cloud-upload-line.svg?v=1761076496" alt="Upload" loading="lazy" style="display: none;">
            <div class="btn-text-container">
              <span class="btn-text">Add to Cart</span>
              <span class="btn-helper-text"></span>
            </div>
          </div>
        </div>
        <div class="sticky-cart-info">
          <div class="sticky-cart-attributes"></div>
          <div class="sticky-cart-price-row">
            <span class="btn-price"></span>
            <span class="btn-quantity"></span>
          </div>
        </div>
      </div>
    `;

    stickyContainer.appendChild(stickyButton);

    // Insert sticky container into product info (not body!)
    productInfoContainer.appendChild(stickyContainer);

    // Update price/quantity info
    updateStickyProductInfo();

    // Sync button clicks between original and sticky button
    setupButtonSync(stickyButton);

    log('✅ Sticky container created within product info');
  };

  const updateStickyProductInfo = () => {
    if (!stickyContainer) {
      log('⚠️ updateStickyProductInfo: No sticky container');
      return;
    }

    log('🔄 updateStickyProductInfo called');

    // Get product price from the actual variant
    // Try multiple container scopes in order of preference
    const containers = [
      document.querySelector('.product__info-wrapper'),
      document.querySelector('.product__info-container'),
      document.querySelector('.product__info'),
      document.querySelector('.product-form')
    ].filter(Boolean);

    let productPrice = null;
    let foundIn = null;

    // Try each container until we find a price
    for (const container of containers) {
      // IMPORTANT: Try .price.price--medium FIRST (visible user-facing price)
      // Then fall back to other selectors
      productPrice = container.querySelector('.price.price--medium .price-item--regular') ||
                    container.querySelector('.price.price--medium .price-item') ||
                    container.querySelector('.price__regular .price-item--regular') ||
                    container.querySelector('.price-item--regular') ||
                    container.querySelector('.price-item') ||
                    container.querySelector('.price');
      if (productPrice) {
        foundIn = container.className;
        log('✅ Found price within:', foundIn);
        break;
      }
    }

    // If still not found, try page-level search
    if (!productPrice) {
      productPrice = document.querySelector('.price.price--medium .price-item--regular') ||
                    document.querySelector('.price.price--medium .price-item') ||
                    document.querySelector('.price__regular .price-item--regular') ||
                    document.querySelector('.price-item--regular') ||
                    document.querySelector('.price-item');
      if (productPrice) {
        foundIn = 'page-level (fallback)';
        log('⚠️ Price found at page level (not in product container)');
      }
    }

    const priceElement = stickyContainer.querySelector('.btn-price');

    log('🔍 Price elements found:', {
      foundIn: foundIn,
      productPrice: productPrice,
      priceElement: priceElement,
      productPriceText: productPrice?.textContent?.trim(),
      currentStickyPriceText: priceElement?.textContent
    });

    if (productPrice && priceElement) {
      const newPrice = productPrice.textContent.trim();
      const oldPrice = priceElement.textContent;
      priceElement.textContent = newPrice;
      log('✅ Updated sticky price:', { from: oldPrice, to: newPrice });
    } else {
      log('⚠️ Missing elements:', {
        hasProductPrice: !!productPrice,
        hasPriceElement: !!priceElement
      });
    }

    // Update product attributes
    const attributesElement = stickyContainer.querySelector('.sticky-cart-attributes');
    if (attributesElement) {
      const attributes = getCurrentVariantAttributes();
      if (attributes && attributes.allOptions) {
        // Strategy: Keep last option on its own line, combine first options on first line
        // This works for both:
        // - Sides • Size | Base Options (feather flags)
        // - Product Options | Sides • Size (fabric banners with product options first)
        const lines = [];

        if (attributes.allOptions.length === 0) {
          attributesElement.textContent = 'Standard';
        } else if (attributes.allOptions.length === 1) {
          // Single option - just display it
          attributesElement.textContent = attributes.allOptions[0].value;
        } else if (attributes.allOptions.length === 2) {
          // Two options - put on same line with bullet
          attributesElement.textContent = attributes.allOptions.map(opt => opt.value).join(' • ');
        } else {
          // Three or more options - first line gets all but last, second line gets last option
          lines.push(attributes.allOptions.slice(0, -1).map(opt => opt.value).join(' • '));
          lines.push(attributes.allOptions[attributes.allOptions.length - 1].value);

          attributesElement.innerHTML = lines
            .map(line => `<div class="attribute-line">${line}</div>`)
            .join('');
        }

        log('✅ Updated sticky attributes:', attributes.display);
      } else if (attributes && attributes.display) {
        // Fallback to single line if allOptions not available
        attributesElement.textContent = attributes.display;
        log('✅ Updated sticky attributes:', attributes.display);
      }
    }

    // Update quantity from actual quantity input
    const quantityElement = stickyContainer.querySelector('.btn-quantity');
    const quantityInput = document.querySelector('input[name="quantity"]');
    if (quantityElement && quantityInput) {
      const qty = parseInt(quantityInput.value) || 1;
      quantityElement.textContent = `${qty} pc${qty !== 1 ? 's' : ''}`;
      log('✅ Updated sticky quantity:', qty);
    }
  };

  const setupButtonSync = (stickyButton) => {
    // Store reference to sticky button globally
    window._stickyButton = stickyButton;

    // State-aware click handler for sticky button
    stickyButton.addEventListener('click', (e) => {
      e.preventDefault();

      log('🖱️ Sticky button clicked, current state:', currentState);

      // Handle click based on current state
      if (currentState === ENHANCED_STATES.NEED_DESIGN) {
        // State: Need design - trigger PitchPrint design button
        log('🎨 Triggering PitchPrint design button');
        const pitchPrintButton = document.querySelector('#pp_customize_design_btn, .pp-customize-btn, [data-pitchprint-button]');
        if (pitchPrintButton) {
          pitchPrintButton.click();
        } else {
          log('⚠️ PitchPrint button not found');
        }
      } else if (currentState === ENHANCED_STATES.NEED_FILE_UPLOAD) {
        // State: Need upload - scroll to file upload section with validation styling
        log('📤 Scrolling to file upload section with validation styling');
        const fileUploadSection = document.querySelector('#custom-file-uploader-container, .custom-file-uploader, .file-upload-wrapper');
        if (fileUploadSection) {
          // Add validation error class to show purple gradient and badge
          const wrapper = document.querySelector('.custom-file-uploader-wrapper');
          if (wrapper) {
            wrapper.classList.add('validation-error');

            // Remove error state when user interacts with upload section
            const removeError = () => {
              wrapper.classList.add('interaction-started');
              setTimeout(() => {
                wrapper.classList.remove('validation-error', 'interaction-started');
              }, 300);
              wrapper.removeEventListener('click', removeError);
              wrapper.removeEventListener('change', removeError);
            };

            wrapper.addEventListener('click', removeError, { once: true });
            wrapper.addEventListener('change', removeError, { once: true });
          }

          // Scroll to the upload section
          fileUploadSection.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Add pulse animation to match unified system behavior
          fileUploadSection.style.animation = 'pulse 1s ease-in-out 2';

          log('📁 Scrolled to file upload section with validation styling');
        } else {
          log('⚠️ File upload section not found');
        }
      } else if (currentState === ENHANCED_STATES.READY_TO_ADD) {
        // State: Ready to add - trigger actual add to cart
        log('🛒 Triggering add to cart');
        addToCartButton.click();
      } else {
        // All other states - default to add to cart button behavior
        log('🔄 Default: Triggering add to cart button');
        addToCartButton.click();
      }
    });

    // DEBUG: Try to find all possible price elements
    log('🔍 DEBUG: Searching for price elements...');

    // Try multiple container scopes (same as updateStickyProductInfo)
    const containers = [
      document.querySelector('.product__info-wrapper'),
      document.querySelector('.product__info-container'),
      document.querySelector('.product__info'),
      document.querySelector('.product-form')
    ].filter(Boolean);

    let priceElement = null;
    let foundInContainer = null;

    // Try each container until we find a price element
    for (const container of containers) {
      // IMPORTANT: Observe .price.price--medium FIRST (visible user-facing price container)
      priceElement = container.querySelector('.price.price--medium') ||
                    container.querySelector('.price__regular') ||
                    container.querySelector('.price');
      if (priceElement) {
        foundInContainer = container.className;
        log('✅ Found price container in:', foundInContainer);
        break;
      }
    }

    // If still not found, try page-level search
    if (!priceElement) {
      priceElement = document.querySelector('.price.price--medium') ||
                    document.querySelector('.price__regular') ||
                    document.querySelector('.price');
      if (priceElement) {
        foundInContainer = 'page-level (fallback)';
        log('⚠️ Price container found at page level');
      }
    }

    log('🔍 DEBUG: Found price element for observation:', {
      foundInContainer: foundInContainer,
      priceElement: priceElement?.className,
      hasElement: !!priceElement
    });

    if (priceElement) {
      log('✅ Price element found for observation:', priceElement);
      log('🔍 Price element HTML:', priceElement.outerHTML.substring(0, 200));
      log('🔍 Current price text:', priceElement.textContent.trim());

      // Debounce timer for price updates
      let priceUpdateTimeout;

      const priceObserver = new MutationObserver((mutations) => {
        log('💰 Price element changed! Mutation detected:');
        mutations.forEach(mutation => {
          log('  - Type:', mutation.type);
          log('  - Target:', mutation.target);
          if (mutation.type === 'childList') {
            log('  - Added nodes:', mutation.addedNodes.length);
            log('  - Removed nodes:', mutation.removedNodes.length);
          }
          if (mutation.type === 'characterData') {
            log('  - Old value:', mutation.oldValue);
            log('  - New value:', mutation.target.textContent);
          }
        });

        // IMPORTANT: Debounce the update to allow Shopify to finish updating the DOM
        clearTimeout(priceUpdateTimeout);
        priceUpdateTimeout = setTimeout(() => {
          log('🔄 Updating sticky button price now (after debounce)...');
          updateStickyProductInfo();
        }, 150); // Wait 150ms for Shopify to complete DOM updates
      });

      priceObserver.observe(priceElement, {
        childList: true,
        subtree: true,
        characterData: true,
        characterDataOldValue: true
      });

      log('👁️ Price MutationObserver attached to:', priceElement.className);
    } else {
      log('⚠️ No price element found for observation');
    }

    // ALSO: Try listening to variant-selects as a fallback test
    log('🔍 DEBUG: Looking for variant-selects...');
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      log('✅ Found variant-selects element');

      let variantChangeTimeout;

      variantSelects.addEventListener('change', () => {
        log('🔄 variant-selects change event fired');

        // Store the current price before the change
        const priceBeforeChange = stickyContainer?.querySelector('.btn-price')?.textContent || '';
        log('💰 Price before variant change:', priceBeforeChange);

        // Update sticky button info after a short delay for Shopify to update prices
        clearTimeout(variantChangeTimeout);
        variantChangeTimeout = setTimeout(() => {
          log('🔄 Updating product info after variant change');
          updateStickyProductInfo();

          // VERIFICATION: Check if price actually changed, if not, retry
          setTimeout(() => {
            const priceAfterChange = stickyContainer?.querySelector('.btn-price')?.textContent || '';
            log('💰 Price after update:', priceAfterChange);

            // If price didn't change from the old value, Shopify might still be updating
            if (priceBeforeChange && priceAfterChange === priceBeforeChange) {
              log('⚠️ Price unchanged after update - retrying in 200ms');
              setTimeout(() => {
                log('🔄 RETRY: Updating sticky button price again');
                updateStickyProductInfo();
              }, 200);
            } else {
              log('✅ Price successfully updated');
            }
          }, 50); // Quick check after update
        }, 500); // Increased to 500ms to allow card-variants + Shopify DOM updates to complete
      });
    } else {
      log('⚠️ No variant-selects element found');
    }

    // IMPORTANT: Also check price after initial page load (for card-variants popular variant)
    setTimeout(() => {
      log('🔄 Initial price recheck after page load (for card-variants)');
      updateStickyProductInfo();
    }, 1000); // Wait 1 second for card-variants to apply popular variants
  };

  // NEW: Setup main enhanced button click handlers
  const setupMainEnhancedButtonHandlers = () => {
    if (!mainEnhancedButton) {
      log('⚠️ No main enhanced button found - skipping handler setup');
      return;
    }

    // State-aware click handler for main enhanced button
    mainEnhancedButton.addEventListener('click', (e) => {
      log('🖱️ Main enhanced button clicked, current state:', currentState);

      // Handle click based on current state
      if (currentState === ENHANCED_STATES.NEED_DESIGN) {
        // State: Need design - trigger PitchPrint design button
        e.preventDefault();
        log('🎨 Triggering PitchPrint design button');
        const pitchPrintButton = document.querySelector('#pp_customize_design_btn, .pp-customize-btn, [data-pitchprint-button]');
        if (pitchPrintButton) {
          pitchPrintButton.click();
        } else {
          log('⚠️ PitchPrint button not found');
        }
      } else if (currentState === ENHANCED_STATES.NEED_FILE_UPLOAD) {
        // State: Need upload - scroll to file upload section
        e.preventDefault();
        log('📤 Scrolling to file upload section');
        const fileUploadSection = document.querySelector('#custom-file-uploader-container, .custom-file-uploader, .file-upload-wrapper');
        if (fileUploadSection) {
          fileUploadSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Optional: Add a visual pulse to highlight the upload area
          fileUploadSection.style.animation = 'pulse 1s ease-in-out 2';
        } else {
          log('⚠️ File upload section not found');
        }
      } else if (currentState === ENHANCED_STATES.QUICK_CHOOSE_FRONT) {
        // State: Quick Design - Choose Front - scroll to front design section
        e.preventDefault();
        log('🎨 Quick Design: Scrolling to front design section');
        scrollToDesignRadioSection('front');
      } else if (currentState === ENHANCED_STATES.QUICK_CHOOSE_BACK) {
        // State: Quick Design - Choose Back - scroll to back design section
        e.preventDefault();
        log('🔄 Quick Design: Scrolling to back design section');
        scrollToDesignRadioSection('back');
      } else if (currentState === ENHANCED_STATES.QUICK_PREVIEW) {
        // State: Quick Design - Preview - trigger preview button
        e.preventDefault();
        log('👀 Quick Design: Triggering preview button');
        triggerDesignRadioPreview();
      }
      // For READY_TO_ADD and other states, allow default form submission behavior
    });

    log('✅ Main enhanced button click handlers attached');
  };

  // =====================================
  // QUICK DESIGN FLOW HELPERS (v4.2.0)
  // =====================================

  /**
   * Scroll to the Design Radio Selector section (front or back)
   * @param {string} side - 'front' or 'back'
   */
  const scrollToDesignRadioSection = (side) => {
    const sectionId = side === 'back' ? 'design-radio-back' : 'design-radio-front';
    const section = document.getElementById(sectionId);

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Add attention pulse animation
      section.classList.add('attention-pulse');
      setTimeout(() => section.classList.remove('attention-pulse'), 2000);

      log(`✅ Scrolled to ${side} design section`);
    } else {
      log(`⚠️ Design Radio section not found: #${sectionId}`);
    }
  };

  /**
   * Trigger the Design Radio Selector preview button
   */
  const triggerDesignRadioPreview = () => {
    const previewBtn = document.getElementById('design-radio-preview-btn');

    if (previewBtn && !previewBtn.disabled) {
      previewBtn.click();
      log('✅ Triggered Design Radio preview button');
    } else {
      log('⚠️ Design Radio preview button not found or disabled');
    }
  };

  /**
   * Handle Quick Design step changes from StateManager
   * @param {CustomEvent} event - Event with step data in event.detail
   */
  const handleQuickDesignStepChange = (event) => {
    const { step, frontSelected, backSelected, isDoubleSided } = event.detail;

    log('📡 Quick Design step changed:', { step, frontSelected, backSelected, isDoubleSided });

    const stateMap = {
      'choose-front': ENHANCED_STATES.QUICK_CHOOSE_FRONT,
      'choose-back': ENHANCED_STATES.QUICK_CHOOSE_BACK,
      'preview': ENHANCED_STATES.QUICK_PREVIEW,
      'ready': ENHANCED_STATES.READY_TO_ADD
    };

    const mappedState = stateMap[step];
    if (mappedState) {
      setState(mappedState);
    }
  };

  // NEW: Update both sticky AND main enhanced buttons
  const updateBothButtons = () => {
    updateStickyButton();
    updateMainEnhancedButton();
  };

  const updateStickyButton = () => {
    const stickyButton = window._stickyButton;
    if (!stickyButton || !STATE_CONFIG[currentState]) return;

    const config = STATE_CONFIG[currentState];
    stickyButton.disabled = !config.buttonEnabled;

    // Update button text (not the entire button content, just the text)
    const btnText = stickyButton.querySelector('.btn-text');
    if (btnText) {
      btnText.textContent = config.buttonText;
    }

    // Update helper text based on state
    const btnHelperText = stickyButton.querySelector('.btn-helper-text');
    if (btnHelperText) {
      // Use helperText from STATE_CONFIG if available
      if (config.helperText) {
        btnHelperText.textContent = config.helperText;
        btnHelperText.style.display = 'block';
      } else if (currentState === ENHANCED_STATES.NEED_DESIGN) {
        btnHelperText.textContent = 'Choose a template or start from scratch';
        btnHelperText.style.display = 'block';
      } else if (currentState === ENHANCED_STATES.NEED_FILE_UPLOAD) {
        btnHelperText.textContent = 'Upload your artwork to continue';
        btnHelperText.style.display = 'block';
      } else if (currentState === ENHANCED_STATES.SENDING_LATER) {
        btnHelperText.textContent = 'You\'re sending artwork later';
        btnHelperText.style.display = 'block';
      } else {
        btnHelperText.textContent = '';
        btnHelperText.style.display = 'none';
      }
    }

    // Update icon visibility based on state
    const btnIconCart = stickyButton.querySelector('.btn-icon-cart');
    const btnIconDesign = stickyButton.querySelector('.btn-icon-design');
    const btnIconUpload = stickyButton.querySelector('.btn-icon-upload');

    // Hide all icons first
    if (btnIconCart) btnIconCart.style.display = 'none';
    if (btnIconDesign) btnIconDesign.style.display = 'none';
    if (btnIconUpload) btnIconUpload.style.display = 'none';

    // Show the appropriate icon based on state
    if (currentState === ENHANCED_STATES.NEED_DESIGN ||
        currentState === ENHANCED_STATES.QUICK_CHOOSE_FRONT ||
        currentState === ENHANCED_STATES.QUICK_CHOOSE_BACK ||
        currentState === ENHANCED_STATES.QUICK_PREVIEW) {
      // Design-related states show design icon
      if (btnIconDesign) btnIconDesign.style.display = 'inline-flex';
    } else if (currentState === ENHANCED_STATES.NEED_FILE_UPLOAD) {
      if (btnIconUpload) btnIconUpload.style.display = 'inline-flex';
    } else if (currentState === ENHANCED_STATES.READY_TO_ADD ||
               currentState === ENHANCED_STATES.FILES_UPLOADED ||
               currentState === ENHANCED_STATES.SENDING_LATER ||
               currentState === ENHANCED_STATES.STANDARD_PRODUCT ||
               currentState === ENHANCED_STATES.PROCESSING ||
               currentState === ENHANCED_STATES.SUCCESS) {
      if (btnIconCart) btnIconCart.style.display = 'inline-flex';
    }

    // Update classes
    Object.values(ENHANCED_STATES).forEach(state => {
      const className = STATE_CONFIG[state]?.buttonClass;
      if (className) {
        stickyButton.classList.remove(className);
      }
    });
    stickyButton.classList.add(config.buttonClass);

    // Sync price and quantity from original button if available
    if (addToCartButton) {
      // Try multiple container scopes (same as updateStickyProductInfo)
      const containers = [
        document.querySelector('.product__info-wrapper'),
        document.querySelector('.product__info-container'),
        document.querySelector('.product__info'),
        document.querySelector('.product-form')
      ].filter(Boolean);

      let originalPrice = null;

      // Try each container until we find a price
      for (const container of containers) {
        // IMPORTANT: Try .price.price--medium FIRST (visible user-facing price)
        originalPrice = container.querySelector('.price.price--medium .price-item--regular') ||
                       container.querySelector('.price.price--medium .price-item') ||
                       container.querySelector('.price__regular .price-item--regular') ||
                       container.querySelector('.price-item--regular') ||
                       container.querySelector('.price-item') ||
                       container.querySelector('.price');
        if (originalPrice) break;
      }

      // If still not found, try page-level search
      if (!originalPrice) {
        originalPrice = document.querySelector('.price.price--medium .price-item--regular') ||
                       document.querySelector('.price.price--medium .price-item') ||
                       document.querySelector('.price__regular .price-item--regular') ||
                       document.querySelector('.price-item--regular') ||
                       document.querySelector('.price-item');
      }

      const stickyPrice = stickyButton.querySelector('.btn-price');

      if (originalPrice && stickyPrice) {
        stickyPrice.textContent = originalPrice.textContent.trim();
      }
    }
  };

  // NEW: Ensure enhanced structure exists in main button
  const ensureEnhancedStructure = () => {
    if (!mainEnhancedButton) return false;

    // Check if enhanced structure already exists
    const existingStructure = mainEnhancedButton.querySelector('.enhanced-cart-content');
    if (existingStructure) {
      log('✅ Enhanced structure already exists in main button');
      return true;
    }

    log('⚠️ Enhanced structure missing - injecting now...');

    // Save any existing button text
    const existingText = mainEnhancedButton.textContent?.trim() || 'Add to cart';

    // Create the enhanced structure
    const enhancedStructure = `
      <div class="enhanced-cart-content">
        <div class="enhanced-cart-main">
          <div class="enhanced-cart-main-content">
            <span class="btn-icon btn-icon-cart" style="display: none;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            </span>
            <img class="btn-icon btn-icon-design" src="https://cdn.shopify.com/s/files/1/0828/3865/1157/files/paintbrush.svg?v=1761076852" alt="Design Studio" loading="lazy" style="display: none;">
            <img class="btn-icon btn-icon-upload" src="https://cdn.shopify.com/s/files/1/0828/3865/1157/files/cloud-upload-line.svg?v=1761076496" alt="Upload" loading="lazy" style="display: none;">
            <div class="btn-text-container">
              <span class="btn-text">${existingText}</span>
              <span class="btn-helper-text"></span>
            </div>
          </div>
        </div>
        <div class="enhanced-cart-info">
          <div class="enhanced-cart-attributes"></div>
          <div class="enhanced-cart-price-row">
            <span class="btn-price"></span>
            <span class="btn-quantity"></span>
          </div>
        </div>
      </div>
    `;

    // Inject the structure
    mainEnhancedButton.innerHTML = enhancedStructure;

    log('✅ Enhanced structure injected successfully');
    return true;
  };

  // NEW: Update main enhanced button (duplicates sticky button functionality)
  const updateMainEnhancedButton = () => {
    // ALWAYS ensure we have the current button reference and structure
    const currentButton = document.querySelector('#main-enhanced-cart-button');
    if (!currentButton) {
      log('❌ Button not found in DOM - cannot update');
      return;
    }

    // Update our global reference
    mainEnhancedButton = currentButton;
    addToCartButton = currentButton;

    if (!STATE_CONFIG[currentState]) return;

    // ALWAYS ensure enhanced structure exists before any update
    if (!ensureEnhancedStructure()) {
      log('❌ Could not ensure enhanced structure - skipping update');
      return;
    }

    const config = STATE_CONFIG[currentState];
    mainEnhancedButton.disabled = !config.buttonEnabled;

    // Update button text
    const btnText = mainEnhancedButton.querySelector('.btn-text');
    if (btnText) {
      btnText.textContent = config.buttonText;
    }

    // Update helper text based on state
    const btnHelperText = mainEnhancedButton.querySelector('.btn-helper-text');
    if (btnHelperText) {
      // Use helperText from STATE_CONFIG if available
      if (config.helperText) {
        btnHelperText.textContent = config.helperText;
        btnHelperText.style.display = 'block';
      } else if (currentState === ENHANCED_STATES.NEED_DESIGN) {
        btnHelperText.textContent = 'Choose a template or start from scratch';
        btnHelperText.style.display = 'block';
      } else if (currentState === ENHANCED_STATES.NEED_FILE_UPLOAD) {
        btnHelperText.textContent = 'Upload your artwork to continue';
        btnHelperText.style.display = 'block';
      } else if (currentState === ENHANCED_STATES.SENDING_LATER) {
        btnHelperText.textContent = 'You\'re sending artwork later';
        btnHelperText.style.display = 'block';
      } else {
        btnHelperText.textContent = '';
        btnHelperText.style.display = 'none';
      }
    }

    // Update icon visibility based on state
    const btnIconCart = mainEnhancedButton.querySelector('.btn-icon-cart');
    const btnIconDesign = mainEnhancedButton.querySelector('.btn-icon-design');
    const btnIconUpload = mainEnhancedButton.querySelector('.btn-icon-upload');

    log(`🎨 Icon elements found:`, {
      cart: !!btnIconCart,
      design: !!btnIconDesign,
      upload: !!btnIconUpload,
      currentState
    });

    // Hide all icons first
    if (btnIconCart) btnIconCart.style.display = 'none';
    if (btnIconDesign) btnIconDesign.style.display = 'none';
    if (btnIconUpload) btnIconUpload.style.display = 'none';

    // Show the appropriate icon based on state
    if (currentState === ENHANCED_STATES.NEED_DESIGN) {
      if (btnIconDesign) {
        btnIconDesign.style.display = 'inline-flex';
        log('🎨 Showing design icon (paintbrush)');
      }
    } else if (currentState === ENHANCED_STATES.NEED_FILE_UPLOAD) {
      if (btnIconUpload) {
        btnIconUpload.style.display = 'inline-flex';
        log('📤 Showing upload icon');
      }
    } else if (currentState === ENHANCED_STATES.READY_TO_ADD ||
               currentState === ENHANCED_STATES.FILES_UPLOADED ||
               currentState === ENHANCED_STATES.SENDING_LATER ||
               currentState === ENHANCED_STATES.STANDARD_PRODUCT ||
               currentState === ENHANCED_STATES.PROCESSING ||
               currentState === ENHANCED_STATES.SUCCESS) {
      if (btnIconCart) {
        btnIconCart.style.display = 'inline-flex';
        log('🛒 Showing cart icon');
      }
    }

    // Update classes
    Object.values(ENHANCED_STATES).forEach(state => {
      const className = STATE_CONFIG[state]?.buttonClass;
      if (className) {
        mainEnhancedButton.classList.remove(className);
      }
    });
    mainEnhancedButton.classList.add(config.buttonClass);

    // Update product attributes display
    updateMainEnhancedAttributes();

    // Update price and quantity info
    updateMainEnhancedProductInfo();

    log('Main enhanced button state updated:', currentState);
  };

  // NEW: Update main enhanced button's product attributes
  const updateMainEnhancedAttributes = () => {
    if (!mainEnhancedButton) return;

    const attributesContainer = mainEnhancedButton.querySelector('.enhanced-cart-attributes');
    if (!attributesContainer) return;

    const { allOptions, display } = getCurrentVariantAttributes();

    if (allOptions.length === 0) {
      attributesContainer.innerHTML = '';
      attributesContainer.style.display = 'none';
      return;
    }

    attributesContainer.style.display = 'flex';

    // Format display based on number of options
    if (allOptions.length === 1) {
      // Single option: just show the value
      attributesContainer.innerHTML = `<span class="attribute-single">${allOptions[0].value}</span>`;
    } else if (allOptions.length === 2) {
      // Two options: show on same line with bullet
      attributesContainer.innerHTML = `<span class="attribute-line">${display}</span>`;
    } else {
      // Three or more options: first N-1 on line 1, last on line 2
      const firstOptions = allOptions.slice(0, -1).map(opt => opt.value).join(' • ');
      const lastOption = allOptions[allOptions.length - 1].value;
      attributesContainer.innerHTML = `
        <span class="attribute-line">${firstOptions}</span>
        <span class="attribute-line">${lastOption}</span>
      `;
    }
  };

  // NEW: Update main enhanced button's price and quantity
  const updateMainEnhancedProductInfo = () => {
    if (!mainEnhancedButton) return;

    const priceContainer = mainEnhancedButton.querySelector('.btn-price');
    const quantityContainer = mainEnhancedButton.querySelector('.btn-quantity');

    if (!priceContainer && !quantityContainer) return;

    // Get price from multiple possible containers
    const containers = [
      document.querySelector('.product__info-wrapper'),
      document.querySelector('.product__info-container'),
      document.querySelector('.product__info'),
      document.querySelector('.product-form')
    ].filter(Boolean);

    let priceElement = null;

    // Try each container until we find a price
    for (const container of containers) {
      priceElement = container.querySelector('.price.price--medium .price-item--regular') ||
                    container.querySelector('.price.price--medium .price-item') ||
                    container.querySelector('.price__regular .price-item--regular') ||
                    container.querySelector('.price-item--regular') ||
                    container.querySelector('.price-item') ||
                    container.querySelector('.price');
      if (priceElement) break;
    }

    if (priceElement && priceContainer) {
      priceContainer.textContent = priceElement.textContent.trim();
    }

    // Get quantity
    const quantityInput = document.querySelector('input[name="quantity"]');
    if (quantityInput && quantityContainer) {
      const qty = parseInt(quantityInput.value) || 1;
      quantityContainer.textContent = `${qty} pc${qty !== 1 ? 's' : ''}`;
    }
  };

  const setupScrollHandler = () => {
    if (!stickyContainer || !addToCartButton) return;

    // Function to update sticky button positioning to match product-form width
    const updateStickyPosition = () => {
      const productForm = document.querySelector('.product-form') ||
                         document.querySelector('.product-configurator-wrapper') ||
                         document.querySelector('.product__info-wrapper');

      if (productForm) {
        const rect = productForm.getBoundingClientRect();
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

        stickyContainer.style.left = `${rect.left + scrollLeft}px`;
        stickyContainer.style.width = `${rect.width}px`;
        stickyContainer.style.right = 'auto';
      }
    };

    // Initial positioning
    updateStickyPosition();

    // Update on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateStickyPosition, 100);
    });

    // Remove loading class after a short delay to allow positioning
    setTimeout(() => {
      stickyContainer.classList.remove('configurator-loading');
      updateStickyPosition(); // Ensure position is correct after loading
    }, 500);

    // Use IntersectionObserver to watch the original add-to-cart button
    const originalButtonObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Original button is visible, hide sticky
          stickyContainer.classList.remove('visible');
        } else {
          // Original button is not visible, show sticky
          stickyContainer.classList.add('visible');
          updateStickyPosition(); // Update position when showing
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px' // Show sticky when original is 50px from bottom
    });

    // Observe the original button
    originalButtonObserver.observe(addToCartButton);

    log('✅ IntersectionObserver attached for sticky button visibility');
  };

  // =====================================
  // MOBILE VISIBILITY LOGIC
  // =====================================

  const isMobile = () => {
    return window.innerWidth <= 749; // Shopify mobile breakpoint
  };

  const updateStickyVisibilityForMobile = () => {
    if (!stickyContainer) return;

    // Only apply mobile-specific logic on mobile devices
    if (!isMobile()) {
      // Desktop: Always use default visibility logic (IntersectionObserver)
      stickyContainer.classList.remove('hide-on-mobile');
      return;
    }

    // Mobile logic:
    // Hide sticky button if:
    // 1. Project is saved (toast widget visible with its own add-to-cart)
    // 2. Files are uploaded (toast widget visible)
    // Show sticky button if:
    // 1. No project and no files (need_design or need_file_upload state)

    const hasProject = checkPitchPrintProjectStateSync();
    const hasFiles = checkFileUploadStateSync();

    log('🔍 Mobile visibility check:', {
      isMobile: true,
      hasProject,
      hasFiles,
      currentState
    });

    // If project saved OR files uploaded, hide on mobile (toast is visible)
    if (hasProject || hasFiles) {
      log('📱 Mobile: Hiding sticky (toast widget active)');
      stickyContainer.classList.add('hide-on-mobile');
    } else {
      // No project/files yet, show sticky button on mobile
      log('📱 Mobile: Showing sticky (no toast widget)');
      stickyContainer.classList.remove('hide-on-mobile');
    }
  };

  // Sync versions of state checkers for mobile visibility
  const checkPitchPrintProjectStateSync = () => {
    const pitchPrintInput = document.querySelector('input[name="properties[_pitchprint]"]');
    const editButton = document.querySelector('#pp_edit_btn');
    const downloadButton = document.querySelector('#pp_download_btn');

    const hasProjectId = pitchPrintInput && pitchPrintInput.value && pitchPrintInput.value.trim() !== '';
    const hasActiveProject = (editButton && editButton.style.display !== 'none') ||
                             (downloadButton && downloadButton.style.display !== 'none');

    return hasProjectId || hasActiveProject;
  };

  const checkFileUploadStateSync = () => {
    const fileUploadState = window.PitchPrintStateManager?.getUploadData?.();
    if (fileUploadState) {
      return !!(fileUploadState.files?.front || fileUploadState.files?.back);
    }
    return false;
  };

  // =====================================
  // MOBILE DOCK POSITION ADJUSTMENT
  // =====================================

  const setupMobileDockObserver = () => {
    if (!stickyContainer) return;

    // Only set up on mobile
    if (!isMobile()) {
      log('⏭️ Skipping mobile dock observer (not mobile)');
      return;
    }

    // Find the mobile dock element
    const mobileDock = document.querySelector('.mobile-dock');
    if (!mobileDock) {
      log('⏭️ No mobile dock found (may not be on this page)');
      return;
    }

    log('📱 Setting up mobile dock observer');

    // Function to adjust sticky button position based on dock visibility
    const adjustStickyForDock = () => {
      // Check if dock has is-active class
      const hasActiveClass = mobileDock.classList.contains('is-active');

      // Also check if dock is actually visible by checking its transform
      // When visible: transform is 'matrix(1, 0, 0, 1, 0, 0)' which means translateY(0)
      // When hidden: transform is 'matrix(1, 0, 0, 1, 0, [dock-height])' which means translateY(100%)
      const computedStyle = window.getComputedStyle(mobileDock);
      const transform = computedStyle.transform;

      // Check if transform indicates the dock is at translateY(0)
      const isTransformZero = transform === 'matrix(1, 0, 0, 1, 0, 0)' || transform === 'none';
      const isDockActuallyVisible = hasActiveClass && isTransformZero;

      if (isDockActuallyVisible) {
        // Dock is visible - move sticky button up by dock height
        const dockHeight = mobileDock.getBoundingClientRect().height;
        stickyContainer.style.bottom = `${dockHeight}px`;
        log(`📱 Dock visible - moved sticky up by ${dockHeight}px (transform: ${transform})`);
      } else {
        // Dock is hidden - reset sticky button to bottom
        stickyContainer.style.bottom = '0px';
        log(`📱 Dock hidden - reset sticky to bottom (hasActive: ${hasActiveClass}, transform: ${transform})`);
      }
    };

    // Initial adjustment
    adjustStickyForDock();

    // Watch for dock visibility changes (is-active class changes)
    const dockObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Use setTimeout to wait for CSS transition to complete
          setTimeout(adjustStickyForDock, 50);
        }
      });
    });

    dockObserver.observe(mobileDock, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Also listen for transitionend event to catch when the slide animation finishes
    mobileDock.addEventListener('transitionend', (e) => {
      // Only react to transform transitions
      if (e.propertyName === 'transform') {
        adjustStickyForDock();
      }
    });

    // Also watch for window resize to recalculate dock height
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isMobile()) {
          adjustStickyForDock();
        } else {
          // Reset to bottom if switched to desktop
          stickyContainer.style.bottom = '0px';
        }
      }, 100);
    });

    log('✅ Mobile dock observer active');
  };

  // =====================================
  // BUTTON REPLACEMENT WATCHER
  // =====================================

  const setupButtonReplacementWatcher = () => {
    // ROBUST: Multiple strategies to ensure structure persists
    // Strategy 1: Direct button monitoring
    // Strategy 2: Variant change event listener
    // Strategy 3: Periodic structure check during critical moments

    if (!mainEnhancedButton) {
      log('⏭️ No main enhanced button found, skipping replacement watcher');
      return;
    }

    log('👁️ Setting up robust button structure watcher');

    // Helper function to check and restore structure
    const checkAndRestoreStructure = (source = 'unknown') => {
      // Always re-fetch button to avoid stale references
      const currentButton = document.querySelector('#main-enhanced-cart-button');
      if (!currentButton) {
        log(`❌ [${source}] Button not found in DOM`);
        return false;
      }

      // Update our global reference
      if (currentButton !== mainEnhancedButton) {
        log(`🔄 [${source}] Button reference updated`);
        mainEnhancedButton = currentButton;
        addToCartButton = currentButton;
      }

      // Check if structure is missing
      const hasStructure = currentButton.querySelector('.enhanced-cart-content');
      if (!hasStructure) {
        log(`⚠️ [${source}] Structure missing - restoring immediately`);

        // Restore structure
        if (ensureEnhancedStructure()) {
          updateMainEnhancedButton();
          log(`✅ [${source}] Structure restored successfully`);
          return true;
        } else {
          log(`❌ [${source}] Failed to restore structure`);
          return false;
        }
      }

      return true; // Structure exists
    };

    // STRATEGY 1: Watch the form container for button changes
    const formContainer = document.querySelector('.product-form__buttons');
    if (formContainer) {
      const formObserver = new MutationObserver((mutations) => {
        // Check if button was modified
        for (const mutation of mutations) {
          if (mutation.type === 'childList' ||
              (mutation.target && mutation.target.id === 'main-enhanced-cart-button')) {
            checkAndRestoreStructure('FormObserver');
            break; // Only check once per batch
          }
        }
      });

      formObserver.observe(formContainer, {
        childList: true,
        subtree: true,
        attributes: false, // Don't watch attributes to avoid throttling
        characterData: false
      });

      log('✅ Form container observer active');
    }

    // STRATEGY 2: Listen for variant change events
    document.addEventListener('variant:change', () => {
      log('🔄 Variant change event detected');
      // Check immediately and after a short delay
      checkAndRestoreStructure('VariantChangeImmediate');
      setTimeout(() => checkAndRestoreStructure('VariantChangeDelayed'), 50);

      // Always update attributes on variant change (even if structure already exists)
      // This ensures attributes display correctly when URL variant is applied
      setTimeout(() => {
        updateMainEnhancedAttributes();
        updateStickyProductInfo();
        log('🔄 Attributes updated after variant change');
      }, 100);
    });

    // STRATEGY 3: Also check on Shopify's variant-selects change event
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      // Use the existing change listener but ensure structure
      const existingChangeHandler = variantSelects.addEventListener;
      variantSelects.addEventListener('change', () => {
        log('🔄 variant-selects change detected');
        checkAndRestoreStructure('VariantSelectsChange');
      });
    }

    log('✅ Robust structure watcher active with 3 strategies');
  };

  // =====================================
  // FILE UPLOAD MONITORING
  // =====================================

  const setupFileUploadMonitoring = () => {
    // REMOVED: DOM mutation observer that was causing race conditions
    // We now rely entirely on StateManager events (upload:completed, upload:cleared, upload:data:updated)
    // which are more reliable and don't have timing issues

    // Keep legacy DOM event listeners for backward compatibility (though not used by current system)
    document.addEventListener('file-upload:completed', async () => {
      log('📤 File upload completed (legacy DOM event)');
      // StateManager events will handle this - no need to do anything here
    });

    document.addEventListener('file-upload:cleared', async () => {
      log('📤 File upload cleared (legacy DOM event)');
      // StateManager events will handle this - no need to do anything here
    });

    log('👁️ File upload monitoring active (StateManager-based)');
  };

  const setupQuantityMonitoring = () => {
    // Monitor quantity changes from dropdown or any other quantity input
    const quantityInput = document.querySelector('input[name="quantity"]');
    if (!quantityInput) {
      log('No quantity input found');
      return;
    }

    // Listen for change events on quantity input
    quantityInput.addEventListener('change', () => {
      log('🔢 Quantity changed:', quantityInput.value);
      updateMainEnhancedButton();
      updateStickyProductInfo();
    });

    // Listen for custom quantity:changed event from QuantitySelector
    if (typeof window.StateManager !== 'undefined') {
      window.StateManager.on('quantity:changed', (event) => {
        log('🔢 StateManager quantity:changed event:', event.detail?.quantity);
        updateMainEnhancedButton();
        updateStickyProductInfo();
      });
    }

    log('👁️ Quantity monitoring active');
  };

  const checkFileUploadState = () => {
    // CRITICAL FIX: Use StateManager as the source of truth for upload state
    // Previous DOM-based checks were unreliable (checked for amazonaws.com but we use img.popsigns.co)

    // Check StateManager for upload data (most reliable)
    const uploadData = stateManager?.getUploadData?.();
    if (uploadData) {
      const hasFrontFile = !!(uploadData.files?.front);
      const hasBackFile = !!(uploadData.files?.back);
      const hasFiles = hasFrontFile || hasBackFile;

      log('File upload state (from StateManager):', {
        hasFrontFile,
        hasBackFile,
        hasFiles,
        uploadId: uploadData.uploadId
      });

      return hasFiles;
    }

    // Fallback: Check form hidden inputs for file URLs (any domain)
    const frontUrlInput = document.querySelector('input[name="properties[_front_file_url]"]');
    const backUrlInput = document.querySelector('input[name="properties[_back_file_url]"]');
    const hasS3Urls = (frontUrlInput && frontUrlInput.value) || (backUrlInput && backUrlInput.value);

    log('File upload state (fallback - form inputs):', { hasS3Urls });
    return hasS3Urls;
  };

  // =====================================
  // ENHANCED STYLING INJECTION
  // =====================================

  const injectEnhancedStyles = () => {
    if (document.getElementById('enhanced-add-to-cart-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'enhanced-add-to-cart-styles';
    styles.textContent = `
      /* Enhanced button states - removed disabled styling for need-design and need-upload */

      .product-form__submit.ready-to-add {
        background: linear-gradient(135deg, #6f08fb 0%, #5b21b6 100%) !important;
        color: white !important;
        cursor: pointer !important;
        animation: pulse-ready 2s ease-in-out infinite !important;
      }

      .product-form__submit.validating {
        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
        color: white !important;
        cursor: not-allowed !important;
        animation: pulse-validating 1.5s ease-in-out infinite !important;
      }

      .product-form__submit.processing {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
        color: white !important;
        cursor: not-allowed !important;
        animation: shimmer-processing 2s linear infinite !important;
      }

      .product-form__submit.success {
        background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
        color: white !important;
        cursor: not-allowed !important;
        animation: bounce-success 0.6s ease-out !important;
      }

      .product-form__submit.error {
        background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
        color: white !important;
        cursor: pointer !important;
        animation: shake-error 0.5s ease-in-out !important;
      }

      /* Sticky Add-to-Cart Container - Reference Design */
      .configurator-sticky-cart-container.right-positioned {
        position: fixed;
        bottom: 0;
        /* left and width will be set dynamically via JavaScript */
        transform: translateY(10px);
        z-index: 90;
        box-sizing: border-box;
        padding: 16px;
        background: linear-gradient(to top, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 70%, transparent 100%);
        backdrop-filter: blur(10px);
        border-top: 1px solid #e2e8f0;
        border-left: 1px solid #e2e8f0;
        border-right: 1px solid #e2e8f0;
        border-radius: 12px 12px 0 0;
        opacity: 0;
        transition: opacity 0.3s ease, transform 0.3s ease, bottom 0.3s ease;
        pointer-events: none;
      }

      /* Hide sticky button completely during loading */
      .configurator-sticky-cart-container.configurator-loading {
        display: none !important;
      }

      /* Visible state - must come after base styles */
      .configurator-sticky-cart-container.right-positioned.visible {
        opacity: 1 !important;
        transform: translateY(0) !important;
        pointer-events: auto !important;
      }

      .configurator-sticky-cart-container.right-positioned .sticky-cart-button {
        width: 100%;
        background: linear-gradient(135deg, #6366f1 0%, #06b6d4 100%);
        border: none;
        border-radius: 12px;
        padding: 26px 20px !important;
        color: white;
        font-family: var(--font-button-family);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.1);
        height: auto !important;
        min-height: auto !important;
        max-height: none !important;
      }

      .configurator-sticky-cart-container.right-positioned .sticky-cart-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
      }

      .configurator-sticky-cart-container.right-positioned .sticky-cart-button:active {
        transform: translateY(0);
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
      }

      .configurator-sticky-cart-container.right-positioned .sticky-cart-button:disabled {
        background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
        cursor: not-allowed;
        transform: none;
        box-shadow: 0 2px 8px rgba(107, 114, 128, 0.2);
      }

      .sticky-cart-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
      }

      .sticky-cart-main {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .sticky-cart-main-content {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .sticky-cart-main .btn-icon {
        display: inline-flex;
        width: 18px;
        height: 19px;
        flex-shrink: 0;
      }

      .sticky-cart-main .btn-icon svg {
        width: 100%;
        height: 100%;
      }

      .sticky-cart-main .btn-icon img {
        width: 100%;
        height: 100%;
        filter: brightness(0) invert(1) !important;
      }

      .btn-icon-design,
      .btn-icon-upload {
        filter: brightness(0) invert(1) !important;
      }

      .btn-text-container {
        display: flex;
        flex-direction: column;
        gap: 2px;
        align-items: flex-start;
        text-align: left;
      }

      .btn-text {
        font-size: 16px;
        font-weight: 600;
        line-height: 1.2;
      }

      .btn-helper-text {
        font-size: 10px;
        font-weight: 400;
        opacity: 0.85;
        line-height: 1.2;
        display: none;
      }

      .sticky-cart-price {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 2px;
      }

      .btn-price {
        font-weight: 700;
        font-size: 16px;
      }

      .btn-quantity {
        font-size: 12px;
        opacity: 0.9;
      }

      /* Product attributes display */
      .sticky-cart-info {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
      }

      .sticky-cart-attributes {
        font-size: 11px;
        opacity: 0.9;
        text-align: right;
        line-height: 1.3;
        max-width: 250px;
      }

      .sticky-cart-attributes .attribute-line {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .sticky-cart-price-row {
        display: flex;
        gap: 8px;
        align-items: baseline;
      }

      /* State-specific button styles */
      .sticky-cart-button.start-designing {
        background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%) !important;
        animation: pulse-design 2s ease-in-out infinite;
      }

      .sticky-cart-button.need-upload {
        background: linear-gradient(135deg, #f59e0b 0%, #ef4444 100%) !important;
      }

      .sticky-cart-button.sending-later {
        background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%) !important;
      }

      .sticky-cart-button.ready-to-add {
        background: linear-gradient(135deg, #6366f1 0%, #06b6d4 100%) !important;
      }

      /* Quick Design Flow States (v4.2.0) */
      .sticky-cart-button.quick-choose-front {
        background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%) !important;
      }

      .sticky-cart-button.quick-choose-back {
        background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%) !important;
      }

      .sticky-cart-button.quick-preview {
        background: linear-gradient(135deg, #6f08fb 0%, #e040fb 100%) !important;
        box-shadow: 0 8px 25px rgba(111, 8, 251, 0.35) !important;
      }

      /* Mobile-specific hiding */
      @media (max-width: 749px) {
        .configurator-sticky-cart-container.hide-on-mobile {
          display: none !important;
        }
      }

      /* Suppress configurator sticky cart when overlays are open */
      .mini-cart--open .configurator-sticky-cart-container.right-positioned.visible,
      .mini-cart--opening .configurator-sticky-cart-container.right-positioned.visible,
      .mini-cart--closing .configurator-sticky-cart-container.right-positioned.visible,
      .menu-drawer--open .configurator-sticky-cart-container.right-positioned.visible,
      .menu-drawer--opening .configurator-sticky-cart-container.right-positioned.visible,
      .menu-drawer--closing .configurator-sticky-cart-container.right-positioned.visible,
      .menu-mobile--open .configurator-sticky-cart-container.right-positioned.visible,
      .menu-mobile--opening .configurator-sticky-cart-container.right-positioned.visible,
      .menu-mobile--closing .configurator-sticky-cart-container.right-positioned.visible,
      .search-modal--open .configurator-sticky-cart-container.right-positioned.visible,
      .quick-view--open .configurator-sticky-cart-container.right-positioned.visible,
      .quick-view--opening .configurator-sticky-cart-container.right-positioned.visible,
      .quick-view--closing .configurator-sticky-cart-container.right-positioned.visible {
        opacity: 0 !important;
        pointer-events: none !important;
        transform: translateY(100%) !important;
        transition: none !important;
        z-index: 1 !important;
      }

      /* Pulse animation for Start Designing state */
      @keyframes pulse-design {
        0%, 100% {
          box-shadow: 0 8px 25px rgba(168, 85, 247, 0.4);
          transform: translateY(0);
        }
        50% {
          box-shadow: 0 12px 35px rgba(236, 72, 153, 0.6);
          transform: translateY(-2px);
        }
      }

      /* Pulse animation for file upload section */
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.8;
          transform: scale(1.02);
        }
      }

      /* Mobile responsive */
      @media screen and (max-width: 749px) {
        .configurator-sticky-cart-container.right-positioned {
          padding: 12px;
        }

        .sticky-cart-content {
          gap: 10px;
        }

        .sticky-cart-main-content {
          align-items: center;
        }

        .btn-text {
          font-size: 12px;
        }

        .btn-price {
          font-size: 14px;
        }

        .btn-quantity {
          font-size: 11px;
        }

        .sticky-cart-attributes {
          font-size: 10px;
          max-width: 180px;
        }

        .sticky-cart-attributes .attribute-line {
          font-size: 9px;
        }
      }

      /* Enhanced animations */
      @keyframes pulse-ready {
        0%, 100% {
          box-shadow: 0 8px 25px rgba(111, 8, 251, 0.3);
          transform: scale(1);
        }
        50% {
          box-shadow: 0 12px 35px rgba(111, 8, 251, 0.5);
          transform: scale(1.02);
        }
      }

      @keyframes pulse-validating {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }

      @keyframes shimmer-processing {
        0% { background-position: -200px 0; }
        100% { background-position: 200px 0; }
      }

      @keyframes bounce-success {
        0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
        40%, 43% { transform: translate3d(0,-8px,0); }
        70% { transform: translate3d(0,-4px,0); }
        90% { transform: translate3d(0,-2px,0); }
      }

      @keyframes shake-error {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
        20%, 40%, 60%, 80% { transform: translateX(2px); }
      }

      /* Enhanced header state transitions */
      .celebration-header {
        transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
      }

      .celebration-header.updating {
        opacity: 0.7;
        transform: translateY(-5px);
      }

      /* Toast integration */
      .enhanced-feedback-toast {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        padding: 12px 16px;
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        font-size: 14px;
        font-weight: 500;
        transform: translateX(400px);
        opacity: 0;
        transition: all 0.3s ease;
        backdrop-filter: blur(10px);
      }

      .enhanced-feedback-toast.show {
        transform: translateX(0);
        opacity: 1;
      }

      .enhanced-feedback-toast.success { border-left: 4px solid #10b981; }
      .enhanced-feedback-toast.error { border-left: 4px solid #ef4444; }
      .enhanced-feedback-toast.warning { border-left: 4px solid #f59e0b; }
      .enhanced-feedback-toast.info { border-left: 4px solid #3b82f6; }
    `;
    document.head.appendChild(styles);
    log('🎨 Enhanced styles injected');
  };

  // =====================================
  // STATE MANAGEMENT
  // =====================================

  const setState = (newState, showToast = false, toastMessage = '') => {
    if (currentState === newState) return;

    const oldState = currentState;
    currentState = newState;
    performanceMetrics.stateChanges++;

    log(`🎛️ State change: ${oldState} → ${newState}`);

    updateButtonState();
    updateCelebrationHeader();

    if (showToast && toastMessage) {
      showSubtleToast(newState, toastMessage);
    }

    // Emit state change event
    document.dispatchEvent(new CustomEvent('enhanced-addtocart:state-changed', {
      detail: { oldState, newState, config: STATE_CONFIG[newState] }
    }));
  };

  const updateButtonState = () => {
    if (!addToCartButton || !STATE_CONFIG[currentState]) return;

    const config = STATE_CONFIG[currentState];

    // Update button properties
    addToCartButton.disabled = !config.buttonEnabled;

    // IMPORTANT: Don't use textContent here - it destroys the nested HTML structure
    // The updateBothButtons() call below will handle updating the text properly
    // via querySelector('.btn-text') for enhanced buttons with complex structure

    // For legacy/simple buttons without nested structure, update text directly
    if (!mainEnhancedButton || addToCartButton !== mainEnhancedButton) {
      // This is a legacy button or a different button, safe to update textContent
      addToCartButton.textContent = config.buttonText;
    }

    // Update classes - remove old state classes, add new one
    Object.values(ENHANCED_STATES).forEach(state => {
      const className = STATE_CONFIG[state]?.buttonClass;
      if (className) {
        addToCartButton.classList.remove(className);
      }
    });
    addToCartButton.classList.add(config.buttonClass);

    // Update accessibility
    addToCartButton.setAttribute('aria-label', config.buttonText);
    addToCartButton.setAttribute('data-enhanced-state', currentState);

    // NEW: Update both sticky button AND main enhanced button
    // This will properly update text via querySelector for enhanced buttons
    updateBothButtons();

    log(`🔘 Button updated: ${config.buttonText} (${config.buttonEnabled ? 'enabled' : 'disabled'})`);
  };

  const updateCelebrationHeader = () => {
    if (!celebrationHeader || !STATE_CONFIG[currentState]) return;

    const config = STATE_CONFIG[currentState];
    performanceMetrics.headerUpdates++;

    // Add updating class for smooth transition
    celebrationHeader.classList.add('updating');

    setTimeout(() => {
      const overlineElement = celebrationHeader.querySelector('.step-overline');
      const titleElement = celebrationHeader.querySelector('.celebration-main-title');
      const subtitleElement = celebrationHeader.querySelector('.celebration-subtitle');

      if (overlineElement) {
        overlineElement.textContent = config.headerOverline;
      }
      if (titleElement) {
        titleElement.textContent = config.headerTitle;
      }
      if (subtitleElement) {
        subtitleElement.textContent = config.headerSubtitle;
      }

      // Remove updating class
      celebrationHeader.classList.remove('updating');

      log(`🎊 Header updated: ${config.headerTitle}`);
    }, 100);

    // NOTE: Main enhanced button shares the same celebration header
    // No need to update separately - both buttons use the header updated above
  };

  // =====================================
  // CELEBRATION HEADER MANAGEMENT
  // =====================================

  const createCelebrationHeader = () => {
    const config = STATE_CONFIG[currentState] || STATE_CONFIG[ENHANCED_STATES.NEED_DESIGN];

    return `
      <div class="celebration-header">
        <div class="step-badge-container">
          <div class="step-overline">${config.headerOverline}</div>
          <h2 class="celebration-main-title">${config.headerTitle}</h2>
        </div>
        <div class="celebration-subtitle">
          ${config.headerSubtitle}
        </div>
      </div>
    `;
  };

  const injectCelebrationHeader = () => {
    if (!addToCartForm) return;

    // Check if header already exists
    celebrationHeader = addToCartForm.querySelector('.celebration-header');
    if (celebrationHeader) {
      log('✅ Celebration header already exists, updating...');
      updateCelebrationHeader();
      return;
    }

    const productFormButtons = addToCartForm.querySelector('.product-form__buttons');
    if (!productFormButtons) {
      log('❌ Cannot find product form buttons for header injection');
      return;
    }

    // Create and insert the header
    const headerElement = document.createElement('div');
    headerElement.innerHTML = createCelebrationHeader();
    celebrationHeader = headerElement.firstElementChild;

    // Insert before buttons
    try {
      productFormButtons.parentElement.insertBefore(celebrationHeader, productFormButtons);
      log('✅ Celebration header injected successfully');
    } catch (error) {
      log('⚠️ Header injection failed:', error);
      // Fallback: append to form
      addToCartForm.appendChild(celebrationHeader);
    }
  };

  // =====================================
  // STATE MANAGER INTEGRATION
  // =====================================

  const setupStateManagerListeners = () => {
    stateManager.on('project:changed', handleProjectChanged);
    stateManager.on('project:cleared', handleProjectCleared);
    stateManager.on('project:restored', handleProjectRestored);
    stateManager.on('system:ready', handleSystemReady);

    // File upload listeners
    stateManager.on('upload:completed', handleUploadCompleted);
    stateManager.on('upload:cleared', handleUploadCleared);
    stateManager.on('upload:data:updated', handleUploadDataUpdated);
    stateManager.on('upload:mode:changed', handleUploadModeChanged);

    // Design method change listener
    stateManager.on('design:method:changed', handleDesignMethodChanged);

    // Hardware-only variant listener
    stateManager.on('variant:hardware-only:changed', handleHardwareOnlyChanged);

    // Quick Design flow listener (v4.2.0)
    stateManager.on('quickdesign:step:changed', handleQuickDesignStepChange);

    // Design path change listener (v4.2.0) - handles switching between Quick/Custom Design
    stateManager.on('design:path:changed', handleDesignPathChanged);

    log('🔗 StateManager listeners attached');
  };

  const handleProjectChanged = async (event) => {
    const projectData = event.detail;
    log('📡 Project changed via StateManager:', projectData);

    clearValidationCache();

    if (projectData?.projectId) {
      // Don't show toast here - toast-preview-system.js handles it via project:changed event
      setState(ENHANCED_STATES.READY_TO_ADD, false, 'Design saved! Ready to add to cart.');
      setProjectIdInForm(projectData.projectId);
    } else {
      await performInitialStateAnalysis();
    }

    // Update mobile visibility (hide sticky on mobile when project saved)
    updateStickyVisibilityForMobile();
  };

  const handleProjectCleared = async () => {
    log('📡 Project cleared via StateManager');
    clearValidationCache();
    setState(ENHANCED_STATES.NEED_DESIGN, false, 'Project cleared. Please start designing again.');

    // Update mobile visibility (show sticky on mobile when project cleared)
    updateStickyVisibilityForMobile();
  };

  const handleProjectRestored = async (event) => {
    const projectData = event.detail;
    log('📡 Project restored via StateManager:', projectData);
    setState(ENHANCED_STATES.READY_TO_ADD, true, 'Design restored! Ready to add to cart.');

    // Update mobile visibility (hide sticky on mobile when project restored)
    updateStickyVisibilityForMobile();
  };

  const handleSystemReady = async () => {
    log('📡 System ready');
    await performInitialStateAnalysis();
  };

  const handleUploadCompleted = async (event) => {
    const uploadData = event.detail;
    log('📡 Upload completed via StateManager:', uploadData);

    clearValidationCache();

    // Check if user selected "Send Files Later"
    const uploadMode = uploadData?.mode || stateManager.getUploadMode();
    log('📧 Upload mode:', uploadMode);

    if (uploadMode === 'send_later') {
      setState(ENHANCED_STATES.SENDING_LATER, false, 'Files will be sent later.');
    } else {
      // Use FILES_UPLOADED state for file upload method
      setState(ENHANCED_STATES.FILES_UPLOADED, true, 'Files uploaded! Ready to add to cart.');
    }

    // Update mobile visibility (hide sticky on mobile when files uploaded)
    updateStickyVisibilityForMobile();
  };

  const handleUploadCleared = async () => {
    log('📡 Upload cleared via StateManager');
    clearValidationCache();

    // Check current design method - if switched to PitchPrint, go to NEED_DESIGN
    // Otherwise, go to NEED_FILE_UPLOAD
    const currentDesignMethod = stateManager?.getDesignMethod ? stateManager.getDesignMethod() : null;

    if (currentDesignMethod === 'pitchprint') {
      // User switched to PitchPrint, so we need design not files
      setState(ENHANCED_STATES.NEED_DESIGN, false, 'Please start designing.');
    } else {
      // Still in file upload mode
      setState(ENHANCED_STATES.NEED_FILE_UPLOAD, false, 'Files cleared. Please upload your artwork.');
    }

    // Update mobile visibility (show sticky on mobile when files cleared)
    updateStickyVisibilityForMobile();
  };

  const handleUploadDataUpdated = async (event) => {
    const uploadData = event.detail;
    log('📡 Upload data updated via StateManager:', uploadData);

    // Re-analyze state to check if files are still uploaded or were deleted
    // This handles cases where individual files are removed
    await performInitialStateAnalysis();

    // Update mobile visibility based on current state
    updateStickyVisibilityForMobile();
  };

  const handleUploadModeChanged = async (event) => {
    const mode = event.detail;
    log('📡 Upload mode changed via StateManager:', mode);

    // Guard: Only respond to upload mode changes when file upload is the active design method.
    // The FileUploader emits upload mode on any variant change (shape/sides), even when
    // the user is in PitchPrint mode. Without this guard, popular variant application
    // on page load causes the button to flip from "Customize Your Design" to "Upload Your Files".
    const designMethod = stateManager?.getDesignMethod ? stateManager.getDesignMethod() : null;
    // If StateManager hasn't been set yet, check DOM toggle as fallback
    let effectiveMethod = designMethod;
    if (!effectiveMethod) {
      const toggleRadio = document.querySelector('#design-method-toggle input[name="design_method"]:checked');
      if (toggleRadio) effectiveMethod = toggleRadio.value;
    }
    if (effectiveMethod && effectiveMethod !== 'file_upload') {
      log('📡 Ignoring upload mode change - active design method is:', effectiveMethod);
      return;
    }

    // Update state based on upload mode
    if (mode === 'send_later') {
      // User selected "Send Files Later" - set state to SENDING_LATER
      setState(ENHANCED_STATES.SENDING_LATER, false, 'You\'ll send files later.');
    } else if (mode === 'upload_now') {
      // User selected "Upload Files Now"
      // Check if files are already uploaded
      const uploadData = stateManager.getUploadData();
      if (uploadData && Object.keys(uploadData.files || {}).length > 0) {
        // Files are uploaded
        setState(ENHANCED_STATES.FILES_UPLOADED, false, 'Files uploaded!');
      } else {
        // No files yet - need upload
        setState(ENHANCED_STATES.NEED_FILE_UPLOAD, false, 'Please upload your files.');
      }
    } else {
      // Mode cleared or unknown - re-analyze
      await performInitialStateAnalysis();
    }

    // Update mobile visibility based on current state
    updateStickyVisibilityForMobile();
  };

  const handleDesignMethodChanged = async (event) => {
    const designMethod = event.detail;
    log('📡 Design method changed via StateManager:', designMethod);

    clearValidationCache();

    // Re-analyze state based on new design method
    await performInitialStateAnalysis();

    // Update mobile visibility
    updateStickyVisibilityForMobile();
  };

  /**
   * Handle hardware-only variant state changes
   * When a hardware-only variant is selected, switch to STANDARD_PRODUCT state
   * When switching back to a customizable variant, re-analyze state
   */
  const handleHardwareOnlyChanged = async (event) => {
    const { isHardwareOnly } = event.detail;
    log('📡 Hardware-only state changed:', isHardwareOnly);

    clearValidationCache();

    if (isHardwareOnly) {
      // Switch to standard product state - no customization required
      setState(ENHANCED_STATES.STANDARD_PRODUCT, false, 'Hardware-only variant selected');
      log('🔧 Switched to STANDARD_PRODUCT state for hardware-only variant');
    } else {
      // Switch back to customizable variant - re-analyze state
      log('🎨 Switched back to customizable variant - re-analyzing state');
      await performInitialStateAnalysis();
    }

    // Update mobile visibility
    updateStickyVisibilityForMobile();
  };

  /**
   * Handle design path changes (v4.2.0)
   * When switching away from Quick Design path, re-analyze state to return to normal flow
   */
  const handleDesignPathChanged = async (event) => {
    const path = event.detail;
    log('📡 Design path changed via StateManager:', path);

    clearValidationCache();

    if (path === 'quick') {
      // Quick Design path - the quickdesign:step:changed listener will handle state updates
      log('🚀 Quick Design path selected - waiting for step change event');
    } else {
      // Custom Design or other path - re-analyze state based on current conditions
      log('🎨 Non-Quick Design path selected - re-analyzing state');
      await performInitialStateAnalysis();
    }

    // Update mobile visibility
    updateStickyVisibilityForMobile();
  };

  // =====================================
  // ENHANCED FORM HANDLING
  // =====================================

  const setupFormHandling = () => {
    if (!addToCartForm || !addToCartButton) return;

    // ARCHITECTURE FIX: Don't intercept form submission - unified system handles all cart logic
    // Enhanced integration is UI-only (sticky button, states, attributes display)
    // addToCartForm.addEventListener('submit', handleEnhancedFormSubmission);
    // addToCartButton.addEventListener('click', handleEnhancedButtonClick);

    // Track button interactions
    addToCartButton.addEventListener('click', () => {
      performanceMetrics.buttonInteractions++;
    });

    log('📋 Enhanced UI tracking attached (form handling delegated to unified system)');
  };

  const handleEnhancedFormSubmission = async (e) => {
    log('📋 Enhanced form submission intercepted');

    try {
      // Validation phase
      setState(ENHANCED_STATES.VALIDATING);
      await new Promise(resolve => setTimeout(resolve, 300));

      const validation = await performValidation();
      if (!validation.isValid) {
        e.preventDefault();
        setState(ENHANCED_STATES.ERROR, true, validation.message);
        return;
      }

      e.preventDefault();

      // Processing phase
      setState(ENHANCED_STATES.PROCESSING);
      const result = await submitToCart(validation.projectData);

      if (result.success) {
        setState(ENHANCED_STATES.SUCCESS, true, 'Successfully added to cart!');

        // Cleanup and reset after success
        setTimeout(() => {
          clearAllProjectData();
          setState(ENHANCED_STATES.NEED_DESIGN);
        }, 2000);

        // Emit cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: result.data
        }));

      } else {
        throw new Error(result.error || 'Submission failed');
      }

    } catch (error) {
      log('❌ Enhanced submission failed:', error);
      stateManager.logError(error, 'handleEnhancedFormSubmission');
      setState(ENHANCED_STATES.ERROR, true, getHumanFriendlyErrorMessage(error));

      // Auto-recover after error
      setTimeout(() => {
        performInitialStateAnalysis();
      }, 3000);
    }
  };

  const handleEnhancedButtonClick = async (e) => {
    if (currentState === ENHANCED_STATES.NEED_DESIGN) {
      e.preventDefault();
      triggerCustomizationPrompt();
      // showSubtleToast('info', 'Please start designing your product first'); // Commented out - button now opens designer directly
      return;
    }

    if (currentState === ENHANCED_STATES.NEED_FILE_UPLOAD) {
      e.preventDefault();
      triggerFileUploadPrompt();
      // showSubtleToast('info', 'Please upload your artwork files first'); // Commented out - button now scrolls to upload section
      return;
    }

    if (!STATE_CONFIG[currentState]?.buttonEnabled) {
      e.preventDefault();
      log('🚫 Button click blocked - button disabled');
      return;
    }

    log('✅ Button click allowed');
  };

  // =====================================
  // ENHANCED PROJECT DETECTION
  // =====================================

  const checkPitchPrintProjectState = async () => {
    // Use StateManager first
    const stateProjectId = stateManager.getProjectId();
    if (stateProjectId) {
      return true;
    }

    // Check for file upload (matches Unified system logic)
    const uploadData = stateManager.getUploadData ? stateManager.getUploadData() : null;
    const uploadMode = stateManager.getUploadMode ? stateManager.getUploadMode() : null;
    if (uploadData || uploadMode === 'send_later') {
      return true;
    }

    // Fallback to existing detection logic
    const pitchPrintInput = document.querySelector('input[name="properties[_pitchprint]"]');
    const pitchPrintPdfInput = document.querySelector('input[name="properties[_pprint-pdf-download]"]');
    const editButton = document.querySelector('#pp_edit_btn');
    const downloadButton = document.querySelector('#pp_download_btn');

    const hasProjectId = pitchPrintInput && pitchPrintInput.value && pitchPrintInput.value.length > 10;
    const hasPdfUrl = pitchPrintPdfInput && pitchPrintPdfInput.value && pitchPrintPdfInput.value.length > 10;
    const hasEditButton = editButton && editButton.style.display !== 'none';
    const hasDownloadButton = downloadButton && downloadButton.style.display !== 'none';

    return hasProjectId || hasPdfUrl || hasEditButton || hasDownloadButton;
  };

  const performInitialStateAnalysis = async () => {
    const hasProject = await checkPitchPrintProjectState();
    const requiresCustomization = isPitchPrintProduct();
    const usesFileUpload = isFileUploadProduct();
    const hasUploadedFiles = checkFileUploadState();
    let uploadMode = stateManager?.getUploadMode ? stateManager.getUploadMode() : null;

    // If uploadMode is null but we're in file upload mode, check the DOM for radio button state
    if (usesFileUpload && !uploadMode) {
      const sendLaterRadio = document.querySelector('input[name="upload_mode"][value="send_later"]');
      if (sendLaterRadio && sendLaterRadio.checked) {
        uploadMode = 'send_later';
      }
    }

    log('🔍 Initial state analysis:', {
      hasProject,
      requiresCustomization,
      usesFileUpload,
      hasUploadedFiles,
      uploadMode,
      currentState
    });

    // v4.2.0: Check Quick Design path FIRST
    // If user is on Quick Design path, use the Quick Design step states
    const designPath = stateManager?.getDesignPath ? stateManager.getDesignPath() : null;
    if (designPath === 'quick' && requiresCustomization) {
      const quickStep = stateManager?.getQuickDesignStep ? stateManager.getQuickDesignStep() : null;
      log('🎨 Quick Design path detected, step:', quickStep);

      if (quickStep) {
        const stateMap = {
          'choose-front': ENHANCED_STATES.QUICK_CHOOSE_FRONT,
          'choose-back': ENHANCED_STATES.QUICK_CHOOSE_BACK,
          'preview': ENHANCED_STATES.QUICK_PREVIEW,
          'ready': ENHANCED_STATES.READY_TO_ADD
        };

        const mappedState = stateMap[quickStep];
        if (mappedState) {
          setState(mappedState);
          return; // Exit early - Quick Design flow handles state
        }
      }
    }

    // Determine state based on design method
    // IMPORTANT: Check file upload FIRST, as products can support both methods
    // Check if user selected "Send Files Later" mode
    if (usesFileUpload && uploadMode === 'send_later') {
      setState(ENHANCED_STATES.SENDING_LATER);
    } else if (usesFileUpload && hasUploadedFiles) {
      // Files uploaded via file upload method - use FILES_UPLOADED state
      setState(ENHANCED_STATES.FILES_UPLOADED);
    } else if (usesFileUpload && !hasUploadedFiles) {
      setState(ENHANCED_STATES.NEED_FILE_UPLOAD);
    } else if (requiresCustomization && !hasProject) {
      setState(ENHANCED_STATES.NEED_DESIGN);
    } else if (hasProject) {
      // PitchPrint design saved - use READY_TO_ADD state
      setState(ENHANCED_STATES.READY_TO_ADD);
    } else if (!requiresCustomization && !usesFileUpload) {
      // Standard product (no customization required) - use STANDARD_PRODUCT state
      setState(ENHANCED_STATES.STANDARD_PRODUCT);
    } else {
      // Fallback to standard product state
      setState(ENHANCED_STATES.STANDARD_PRODUCT);
    }
  };

  // =====================================
  // PITCHPRINT MONITORING
  // =====================================

  const setupPitchPrintMonitoring = () => {
    // Monitor PitchPrint container for changes
    const pitchPrintContainer = document.querySelector('#pp_main_btn_sec');
    if (pitchPrintContainer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'attributes') {
            setTimeout(async () => {
              await performInitialStateAnalysis();
            }, 100);
          }
        });
      });

      observer.observe(pitchPrintContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'value']
      });

      log('👁️ PitchPrint monitoring active');
    }

    // Monitor hidden inputs
    const pitchPrintInput = document.querySelector('input[name="properties[_pitchprint]"]');
    if (pitchPrintInput) {
      pitchPrintInput.addEventListener('change', async () => {
        setTimeout(async () => {
          await performInitialStateAnalysis();
        }, 100);
      });
    }

    // Enhanced PitchPrint integration
    let attempts = 0;
    const maxAttempts = 50;

    const setupHandler = () => {
      attempts++;

      if (window.ppclient) {
        if (window.ppclient.on) {
          window.ppclient.on('project-saved', handlePitchPrintProjectSaved);
          log('✅ PitchPrint project-saved handler attached');
        }

        clearInterval(handlerSetupInterval);
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(handlerSetupInterval);
        log('⚠️ PitchPrint timeout - using StateManager only');
      }
    };

    const handlerSetupInterval = setInterval(setupHandler, 200);
  };

  const handlePitchPrintProjectSaved = async (data) => {
    log('🎉 PitchPrint project saved:', data);

    // 2026-04-30: Auto-saves should not flip enhanced state to READY_TO_ADD or show a
    // "Design saved!" toast. They are PitchPrint internals on a 5-minute interval.
    if (data?.data?.autoSaved || data?.autoSaved) {
      log('⏭️ Skipping autoSaved project-saved — UI reacts to manual saves only');
      return;
    }

    const projectId = data.data?.projectId || data.projectId;

    if (projectId) {
      setState(ENHANCED_STATES.READY_TO_ADD, true, 'Design saved! Ready to add to cart.');
      setProjectIdInForm(projectId);
    }
  };

  // =====================================
  // UTILITY FUNCTIONS
  // =====================================

  const performValidation = async () => {
    const hasProject = await checkPitchPrintProjectState();
    const requiresCustomization = isPitchPrintProduct();
    const usesFileUpload = isFileUploadProduct();
    const hasUploadedFiles = checkFileUploadState();

    if (requiresCustomization && !hasProject) {
      return {
        isValid: false,
        message: 'Please customize your design first'
      };
    }

    if (usesFileUpload && !hasUploadedFiles) {
      return {
        isValid: false,
        message: 'Please upload your artwork files first'
      };
    }

    // Validate form data
    const formData = new FormData(addToCartForm);
    const variantId = formData.get('id');
    if (!variantId) {
      return {
        isValid: false,
        message: 'Please select a product variant'
      };
    }

    return {
      isValid: true,
      projectData: hasProject ? { projectId: await getProjectId() } : null
    };
  };

  const submitToCart = async (projectData) => {
    try {
      const formData = new FormData(addToCartForm);

      if (projectData?.projectId) {
        setProjectIdInForm(projectData.projectId);
        formData.set('properties[_pitchprint]', projectData.projectId);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      return { success: true, data: result };

    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const getProjectId = async () => {
    const stateProjectId = stateManager.getProjectId();
    if (stateProjectId) return stateProjectId;

    // Fallback detection
    const pitchPrintInput = document.querySelector('input[name="properties[_pitchprint]"]');
    if (pitchPrintInput && pitchPrintInput.value) {
      return pitchPrintInput.value;
    }

    return null;
  };

  const isPitchPrintProduct = () => {
    // First, check window.productConfig (most reliable - no timing issues)
    if (window.productConfig && window.productConfig.usesPitchprint !== undefined) {
      const usesPitchprint = window.productConfig.usesPitchprint;
      log('🔍 Window productConfig.usesPitchprint:', usesPitchprint, typeof usesPitchprint);
      if (usesPitchprint === false) {
        log('✅ Product does NOT use PitchPrint (window config)');
        return false;
      }
      if (usesPitchprint === true) {
        log('✅ Product uses PitchPrint (window config)');
        return true;
      }
    }

    // Second, check the metafield value from data attribute (backwards compatibility)
    const form = document.querySelector('form[action*="/cart/add"]');
    log('🔍 Form found:', !!form);

    if (form) {
      log('🔍 Form dataset:', {
        usesPitchprint: form.dataset.usesPitchprint,
        enableFileUpload: form.dataset.enableFileUpload,
        hasUsesPP: form.dataset.usesPitchprint !== undefined,
        hasEnableUpload: form.dataset.enableFileUpload !== undefined
      });

      if (form.dataset.usesPitchprint !== undefined) {
        const usesPitchprint = form.dataset.usesPitchprint;
        log('🔍 Data attribute uses_pitchprint:', usesPitchprint, typeof usesPitchprint);
        // Handle both string 'true'/'false' and boolean values, and empty string
        if (usesPitchprint === 'false' || usesPitchprint === false || usesPitchprint === '') {
          log('✅ Product does NOT use PitchPrint (data attribute)');
          return false;
        }
        if (usesPitchprint === 'true' || usesPitchprint === true) {
          log('✅ Product uses PitchPrint (data attribute)');
          return true;
        }
      }
    }

    // Last resort: DOM detection. IMPORTANT — only use PRODUCT-SCOPED signals.
    // Do NOT test for global PitchPrint signals (site-wide <script src*="pitchprint">
    // tags or window.ppclient) — those exist on EVERY page, so they misclassify
    // any non-PitchPrint product (bundles, standard items) as PitchPrint, stranding
    // it on the "Ready to Design?" state and blocking add-to-cart.
    // NOTE: .custom-tab-body is the variant-selector container (card-variants.liquid)
    // and renders for ANY product with variants — it is NOT PitchPrint-specific, so it
    // must not be used as an indicator. #pp_main_btn_sec and #pitchprint/#_pitchprint
    // only render when uses_pitchprint is true.
    log('⚠️ Metafield not found in window or data attributes, falling back to scoped DOM detection');
    const indicators = [
      () => document.querySelector('#pitchprint, #_pitchprint'),
      () => document.querySelector('#pp_main_btn_sec')
    ];

    return indicators.some(check => {
      try {
        return check();
      } catch (e) {
        return false;
      }
    });
  };

  const isFileUploadProduct = () => {
    // First, check window.productConfig (most reliable - no timing issues)
    if (window.productConfig && window.productConfig.enableFileUpload !== undefined) {
      const enableFileUpload = window.productConfig.enableFileUpload;
      log('🔍 Window productConfig.enableFileUpload:', enableFileUpload, typeof enableFileUpload);
      // Handle boolean values
      if (enableFileUpload === false) {
        log('✅ Product does NOT support file upload (window config)');
        return false;
      }
      if (enableFileUpload === true) {
        log('✅ Product supports file upload (window config)');
        // If metafield says file upload is enabled, check if user has SELECTED file upload method
        // via StateManager or toggle (continue to next checks)
      }
    }

    // Second, check the metafield value from data attribute (backwards compatibility)
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form && form.dataset.enableFileUpload !== undefined) {
      const enableFileUpload = form.dataset.enableFileUpload;
      log('🔍 Data attribute enable_file_upload:', enableFileUpload, typeof enableFileUpload);
      // Handle both string 'true'/'false' and boolean values, and empty string
      if (enableFileUpload === 'false' || enableFileUpload === false || enableFileUpload === '') {
        log('✅ Product does NOT support file upload (data attribute)');
        return false;
      }
      if (enableFileUpload === 'true' || enableFileUpload === true) {
        log('✅ Product supports file upload (data attribute)');
        // If metafield says file upload is enabled, check if user has SELECTED file upload method
        // via StateManager or toggle
      }
    }

    // Second, check StateManager for the current design method (user's choice)
    if (stateManager && stateManager.getDesignMethod) {
      const designMethod = stateManager.getDesignMethod();
      if (designMethod === 'file_upload') {
        return true;
      }
      // If design method is explicitly set to something else, return false
      if (designMethod === 'pitchprint') {
        return false;
      }
    }

    // Third, check the design method toggle radio buttons (reliable on page load)
    const toggleContainer = document.getElementById('design-method-toggle');
    if (toggleContainer) {
      const checkedRadio = toggleContainer.querySelector('input[name="design_method"]:checked');
      if (checkedRadio) {
        const selectedMethod = checkedRadio.value;
        if (selectedMethod === 'file_upload' || selectedMethod === 'upload') {
          return true;
        }
        if (selectedMethod === 'pitchprint') {
          return false;
        }
      }

      // If toggle exists but no radio is checked yet, default to PitchPrint (pitchprint is the default)
      // This prevents showing file upload state during initialization
      return false;
    }

    // If no toggle exists and metafield says file upload is disabled, return false
    if (form && form.dataset.enableFileUpload === 'false') {
      return false;
    }

    // Fallback: Check DOM visibility (least reliable)
    const fileUploadContainer = document.querySelector('#custom-file-uploader-container, .custom-file-uploader');
    if (!fileUploadContainer) return false;

    const isVisible = fileUploadContainer.offsetParent !== null;
    return isVisible;
  };

  const setProjectIdInForm = (projectId) => {
    if (!projectId || !addToCartForm) return;

    let input = addToCartForm.querySelector('input[name="properties[_pitchprint]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_pitchprint]';
      addToCartForm.appendChild(input);
    }
    input.value = projectId;
  };

  const clearAllProjectData = () => {
    stateManager.clearProject();

    if (window.ppclient?._vars) {
      window.ppclient._vars.projectId = null;
    }

    const pitchPrintInputs = addToCartForm.querySelectorAll('input[name*="pitchprint"]');
    pitchPrintInputs.forEach(input => input.value = '');

    clearValidationCache();
  };

  const clearValidationCache = () => {
    validationCache.clear();
  };

  const triggerCustomizationPrompt = () => {
    const customizeBtn = document.querySelector('#pp_customize, #pp_main_btn, .pitchprint-customize-btn');
    if (customizeBtn) {
      customizeBtn.click();
    }
  };

  const triggerFileUploadPrompt = () => {
    const fileUploadBtn = document.querySelector('.file-upload-trigger, input[type="file"]');
    if (fileUploadBtn) {
      fileUploadBtn.click();
    }
  };

  const getHumanFriendlyErrorMessage = (error) => {
    const message = error.message || error.toString();

    if (message.toLowerCase().includes('fetch') || message.toLowerCase().includes('network')) {
      return 'Connection issue. Please check your internet and try again.';
    }
    if (message.toLowerCase().includes('inventory') || message.toLowerCase().includes('stock')) {
      return 'This item may be out of stock. Please refresh the page.';
    }

    return 'Something went wrong. Please try again or contact support.';
  };

  // =====================================
  // SUBTLE TOAST SYSTEM
  // =====================================

  const showSubtleToast = (type, message, duration = 3000) => {
    // Remove existing toasts
    document.querySelectorAll('.enhanced-feedback-toast').forEach(toast => {
      toast.remove();
    });

    const toast = document.createElement('div');
    toast.className = `enhanced-feedback-toast ${type}`;
    toast.textContent = message;

    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('show'), 100);

    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, duration);

    log(`💬 Subtle toast: ${type} - ${message}`);
  };

  // =====================================
  // PUBLIC API
  // =====================================

  window.EnhancedAddToCartStateManager = {
    getState: () => ({
      currentState,
      stepNumber,
      hasProject: stateManager.getProjectId(),
      isPitchPrintProduct: isPitchPrintProduct(),
      isFileUploadProduct: isFileUploadProduct(),
      performanceMetrics
    }),

    setState,
    forceStateAnalysis: performInitialStateAnalysis,
    clearCache: clearValidationCache,

    // Debug methods
    simulateProjectSave: () => {
      const testProject = { projectId: 'test_' + Date.now().toString(36) };
      handlePitchPrintProjectSaved(testProject);
    },

    testValidation: performValidation
  };

  // =====================================
  // INITIALIZATION
  // =====================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 0);
  }

  log('🚀 Enhanced Add-to-Cart Integration loaded and ready for initialization');
})();
